import { useRef } from 'react';
import { useEffectOnce } from 'react-use';
import { rage } from '~/rage';
import { PedHeadPosition } from '~/shared';

const CENTER_ZONE_WIDTH = 300;

const useHeadRotation = () => {
	const currentPosition = useRef<PedHeadPosition>(PedHeadPosition.Initial);
	const isDragging = useRef<boolean>(false);

	useEffectOnce(() => {
		const getPositionFromClientX = (clientX: number): PedHeadPosition => {
			const screenCenterX = window.innerWidth / 2;
			const diff = clientX - screenCenterX;
			const halfZone = CENTER_ZONE_WIDTH / 2;

			if (Math.abs(diff) <= halfZone) {
				return PedHeadPosition.Initial;
			}

			return diff < 0 ? PedHeadPosition.Right : PedHeadPosition.Left;
		};

		const applyPosition = (newPos: PedHeadPosition) => {
			if (currentPosition.current !== newPos) {
				currentPosition.current = newPos;
				rage.characterCreation.setHeadPosition(newPos);
			}
		};

		const handleMouseDown = (e: MouseEvent) => {
			if (e.button !== 0) return;

			if ((e.target as HTMLElement)?.closest('[data-head-rotation-ignore]')) {
				return;
			}

			isDragging.current = true;
			applyPosition(getPositionFromClientX(e.clientX));
			window.addEventListener('mousemove', handleMouseMove);
			window.addEventListener('mouseup', handleMouseUp);
		};

		const handleMouseMove = (e: MouseEvent) => {
			if (!isDragging.current) return;
			applyPosition(getPositionFromClientX(e.clientX));
		};

		const handleMouseUp = () => {
			if (!isDragging.current) return;
			isDragging.current = false;
			applyPosition(PedHeadPosition.Initial);
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};

		window.addEventListener('mousedown', handleMouseDown);

		return () => {
			window.removeEventListener('mousedown', handleMouseDown);
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	});
};

export { useHeadRotation };
